import { getDomain } from '@/helpers/getDomains';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { type EventSourceMessage, fetchEventSource } from '@fortaine/fetch-event-source';
import { logger } from '@heidi/runtime/logger';
import type { components } from '@heidi/schema/ml-scribe';

type ErrorResponse = components['schemas']['ErrorResponse'];

type Params = {
  requestBody?: object | null;
  path: string;
  /** Gets the HTTP status of a 2xx response; any other status rejects. */
  onopen?: (status: number) => void;
  onmessage?: (ev: EventSourceMessage) => void;
  onclose?: () => void;
  onAbort?: () => void;
  // onerror?: (error: string) => void;
  endPointVersion?: 'v1' | 'v2' | 'v2-1';
  abortController: AbortController;
};

/** The error a non-2xx stream rejects with. */
type StreamHttpError = Error & { status: number };

// The message is the backend `detail.type`, or `'StreamHttpError'` when there
// is none (e.g. a WAF page).
const readStreamHttpError = async (response: Response): Promise<StreamHttpError> => {
  let detail: Partial<ErrorResponse['detail']> | undefined;
  try {
    const parsed = JSON.parse(await response.text()) as { detail?: unknown };
    if (parsed.detail && typeof parsed.detail === 'object') {
      detail = parsed.detail;
    }
  } catch {
    detail = undefined;
  }
  const type = typeof detail?.type === 'string' ? detail.type : 'StreamHttpError';
  // `detail.msg` is free-form backend text, so it is not carried.
  return Object.assign(new Error(type), { status: response.status });
};

// A SyntaxError's message quotes the chunk, which can be clinical text, so the
// parse failure is rethrown without it.
const parseJsonChunk = (data: string): string => {
  try {
    const chunk: unknown = JSON.parse(data);
    if (
      typeof chunk === 'object' &&
      chunk !== null &&
      !Array.isArray(chunk) &&
      'data' in chunk &&
      typeof chunk.data === 'string'
    ) {
      return chunk.data;
    }
  } catch {
    // Discard the parser error: its message may contain clinical text.
  }
  throw Object.assign(new Error('StreamPayloadParseError'), {
    name: 'StreamPayloadParseError',
  });
};

export const customFetchEventSource = (param: Params) => {
  const endPointVersion = param.endPointVersion || 'v2';
  const sessionURL = getDomain(endPointVersion) + param.path;
  const token = getAuthTokenForHttpClientSync();

  const requestHeaders: Record<string, string> = {
    ...buildScribeSseHeaders(token),
  };

  //   const requestBody:

  let transformedChar = '';
  // Counts and lengths only: the frames are clinical text.
  let receivedChunkCount = 0;
  let invalidFrameCount = 0;
  const isJsonStream =
    endPointVersion === 'v2-1' || param.path.includes('live-dictation');

  const ctrl = param.abortController;

  //   wrap it in promise
  const promise = new Promise<string>((resolve, reject) => {
    let failed = false;
    const abortHandler = () => {
      failed = true;
      reject(new Error('StreamManuallyAbortedError'));
      param.onAbort?.();
    };
    ctrl.signal.addEventListener('abort', abortHandler);
    // Reject with the error itself; the listener goes first so the abort
    // does not also count as a manual one.
    const fail = (error: unknown) => {
      failed = true;
      ctrl.signal.removeEventListener('abort', abortHandler);
      reject(error instanceof Error ? error : new Error(String(error)));
      ctrl.abort();
    };

    void fetchEventSource(sessionURL, {
      headers: requestHeaders,
      body: JSON.stringify(param.requestBody),
      method: 'POST',
      openWhenHidden: true,
      signal: ctrl.signal,

      onopen: async (response) => {
        if (!response.ok) {
          // Throw, not abort: onerror then rejects with this error. An abort would
          // reject as StreamManuallyAbortedError and count as a cancel.
          throw await readStreamHttpError(response);
        }
        param.onopen?.(response.status);
      },
      onmessage: (ev) => {
        if (failed) return;
        // Accumulate now so `resolve` has every chunk; only the callback is
        // deferred. A parse error throws into the library, which rejects.
        // An empty event on a JSON stream (a keep-alive) carries no chunk.
        if (isJsonStream && ev.data === '') return;
        let message = ev;
        if (isJsonStream) {
          let data: string;
          try {
            data = parseJsonChunk(ev.data);
          } catch (error) {
            invalidFrameCount += 1;
            throw error;
          }
          message = { ...ev, data };
          transformedChar += data;
          receivedChunkCount += 1;
        } else {
          transformedChar += ev.data === '' ? '\n' : ev.data.replace(/ {3}/g, '\n');
        }

        setTimeout(() => {
          try {
            param.onmessage?.(message);
          } catch (error) {
            fail(error);
          }
        }, 0);
      },
      onclose: () => {
        // Close after the deferred onmessage callbacks already queued: the
        // caller sees every chunk first, and a throw from one fails the stream
        // instead of closing it.
        setTimeout(() => {
          if (failed) return;
          ctrl.signal.removeEventListener('abort', abortHandler);
          try {
            param.onclose?.();
          } catch (error) {
            fail(error);
            return;
          }
          resolve(transformedChar);
          ctrl?.abort();
        }, 0);
      },
      onerror: (error) => {
        if (error instanceof Error && error.name === 'StreamPayloadParseError') {
          // Deliver chunks queued before the bad frame, then fail (FIFO timers).
          // Dictation drops callbacks once aborted.
          failed = true;
          if (invalidFrameCount > 0) {
            logger.warn('stream_invalid_frames', {
              errorType: 'StreamPayloadParseError',
              endPointVersion,
              isLiveDictation: param.path.includes('live-dictation'),
              invalidFrameCount,
              receivedChunkCount,
              receivedCharCount: transformedChar.length,
            });
          }
          setTimeout(() => fail(error), 0);
        } else {
          fail(error);
        }
        // A malformed frame is terminal for this attempt, never auto-retried.
        throw error;
      },
    }).catch((error: unknown) => {
      // onerror usually fails the stream first. Settle here only if nothing
      // has, so the wrapper never hangs.
      if (!failed) fail(error);
    });
  });

  return promise;
};
